let punycode = require('punycode');
import {Link} from './links-type';
import {MarksHelper} from '../../../../app/shared/components/single-message-editor/code-mirror-wrapper/marks-helper';
import twttr from 'twitter-text';


export class LinksDetector {

  constructor(editor, listener, isShortnerDisabled) {
    this.editor = editor;
    this.listener = listener;

    this.linksUrlsByIds = {}; // Contains all created links, even those deleted (so that we can handle undo)
    this.marksIdstoLinksIds = {}; // Contains all created marks, even those deleted
    this.currentMarksIds = [];
    this.newMarksIds = [];
    this.currentLinks = [];
    this.newLinks = [];
    this.isShortnerDisabled = isShortnerDisabled;
  }
  
  createLink(link) {
    let newMarkerId = MarksHelper.createLinkMark(this.editor, link.startLineCh, link.endLineCh).id;
    this.linksUrlsByIds[newMarkerId] = link.url;
    this.marksIdstoLinksIds[newMarkerId] = newMarkerId;
    this.newMarksIds.push(newMarkerId);
    link.setId(newMarkerId); // The link id is the id of the first mark that marks this links. If the link is updated, link id and mark id will diverge.
    this.newLinks.push(link);
  }
  
  updateLink(mark, link) {
    // Get link id that marker is pointing to
    let linkId = this.marksIdstoLinksIds[mark.id];
  
    // Replace marker if url has changed
    if (link.url !== this.linksUrlsByIds[linkId]) {
      // Remove old marker and create a new one that points to already existing link
      mark.clear();
      let newMarkId = MarksHelper.createLinkMark(this.editor, link.startLineCh, link.endLineCh).id;
      this.marksIdstoLinksIds[newMarkId] = linkId;
  
      // Update link url
      this.linksUrlsByIds[linkId] = link.url;
      this.newMarksIds.push(newMarkId);
    }
    else {
      this.newMarksIds.push(mark.id);
    }
    link.setId(linkId);
    this.newLinks.push(link);
  }
  
  /**
   * At the end of a links detection cycle, clears all marks whose id 
   * are in currentMarksIds but not in newMarksIds
   */
  cleanMarkers() {
    let doc = this.editor.getDoc();
    let textMarks = doc.getAllMarks();
    for (let i = 0; i < this.currentMarksIds.length; i++) {
      let markId = this.currentMarksIds[i];
      if (!this.newMarksIds.includes(markId)) {
        let mark = textMarks.find(m => m.id === markId);
        if (mark) {
          mark.clear();
        }
      }
    }
  }
  
  /**
   * At the end of a links detection cycle, udpates the LinksHandler
   * with links that have been added, updated and removed
   */
  triggerLinksEvents() {
    for (let i = 0; i < this.currentLinks.length; i++) {
      let linkId = this.currentLinks[i].id;
      let currentLink = this.currentLinks[i];
      let newLink = this.newLinks.find(l => l.id === linkId);
      if (!newLink) {
        this.listener.linkRemoved(currentLink);
      }
      else {
        if (newLink.startRaw !== currentLink.startRaw ||
          newLink.endRaw !== currentLink.endRaw ||
          newLink.url !== currentLink.url) {
          this.listener.linkChanged(newLink);
        }
      }
    }
    for (let i = 0; i < this.newLinks.length; i++) {
      let linkId = this.newLinks[i].id;
      let currentLink = this.currentLinks.find(l => l.id === linkId);
      let newLink = this.newLinks[i];
      if (!currentLink) {
        this.listener.linkAdded(newLink);
      }
    }
  }

  /**
   * Helper method to make sure we only take into account marks that we created
   * (and not for example marks created by the spell checker)
   */
  isLinksDetectorMark(mark) {
    return mark && (this.currentMarksIds.includes(mark.id) || this.newMarksIds.includes(mark.id));
  }
  

  /**
   * Helper function that buils links objects with offsets and url (but no id nor 'wrapped' status)
   * from a line of text.
   * It is important to note that we need start and end offsets expressed in two coordinate systems:
   *  - raw offsets from the start of the document, measured in unicode codepoints;
   *  - lineCh offsets as understood by CodeMirror, hence measured in UCS2 16bit code units 
   */
  linksFromLine(line, lineNr) {
    const resultLinks = [];
    const extractedLinks = twttr.extractUrlsWithIndices(line.text);
    extractedLinks.forEach(extractedLink => {
      // first pass: we initialize offsets for CodeMirror
      const startLineCh = {
        'line': lineNr, 
        'ch': extractedLink.indices[0]
      };
      const endLineCh = {
        'line': lineNr, 
        'ch': extractedLink.indices[1]
      };
      const url = extractedLink.url;
      let link = new Link(startLineCh, endLineCh, url);
      link.setIsShortnerDisabled(this.isShortnerDisabled);
      resultLinks.push(link);
    });

    twttr.modifyIndicesFromUTF16ToUnicode(line.text, extractedLinks);
    for (let i = 0; i < extractedLinks.length; i++) {
      // second pass: let's set the CP offsets from the start of the editor
      const startCP = extractedLinks[i].indices[0];
      const endCP = extractedLinks[i].indices[1];
      resultLinks[i].setRawOffsets(startCP, endCP, this.currentLineOffsetCP)
    }
    return resultLinks;
  }

  /**
   * Entry point to links detection, called on every document change
   */
  detectLinks() {
    this.newMarksIds = [];
    this.newLinks = [];
    this.currentLineOffsetCP = 0;
    const doc = this.editor.getDoc();
    doc.eachLine(line => {
      this.linksFromLine(line, line.lineNo())
          .forEach(link => {

        const marks = doc.findMarks(link.startLineCh, link.endLineCh)
                         .filter(mark => this.isLinksDetectorMark(mark));
        if (marks.length === 0) {
          this.createLink(link);
        } else {
          this.updateLink(marks[0], link);
        }
      });
      const line_length = punycode.ucs2.decode(line.text).length;
      this.currentLineOffsetCP += line_length + 1; // +1 for the newline character
    });
    this.cleanMarkers();
    this.triggerLinksEvents();
    this.currentMarksIds = this.newMarksIds;
    this.currentLinks = this.newLinks;
  }
  
  onLeftMouseButtonDown(marksAtClickPosition) {
    let mark = marksAtClickPosition.filter(mark => this.isLinksDetectorMark(mark))[0];
    if (mark) {
      this.listener.linkClicked(this.marksIdstoLinksIds[mark.id]);
    }
    else {
      this.listener.noLinkClicked();
    }
  }

  initLinksDetection() {
    this.detectLinks();
    this.editor.on("change", () => this.detectLinks());
  }
}
